import { ADJUSTMENT_SPECS, type AdjustmentKind } from './registry';

/** Generic item shape every picker menu row carries. The category
 *  generic narrows per-picker (`AdjustmentCategory` for the Adjust…
 *  picker, `FilterCategory` for the Filter… picker) so each picker
 *  can constrain its label record to just its own categories. */
export interface PickerItem<TCategory extends string> {
  kind: AdjustmentKind;
  title: string;
  category: TCategory;
}

interface PickerMenuProps<TCategory extends string> {
  /** Items pre-grouped by category. Insertion order of the map is
   *  preserved in the rendered output — the caller controls section
   *  ordering. */
  grouped: Map<TCategory, PickerItem<TCategory>[]>;
  /** Display label per category. Sized to the caller's enum so a
   *  missing label is a type error, not a fallback. */
  categoryLabels: Record<TCategory, string>;
  /** Picker closes itself in the caller's flow; this just emits the
   *  chosen item's kind. */
  onPick: (item: PickerItem<TCategory>) => void;
}

/** Shared body for the Adjust… and Filter… pickers. Renders one
 *  uppercase section header per non-empty category followed by a
 *  vertical list of kinds; kinds whose `kind` isn't in
 *  `ADJUSTMENT_SPECS` render as disabled "Soon" rows so the picker
 *  can advertise upcoming filters without crashing on click. */
export function PickerMenu<TCategory extends string>({
  grouped,
  categoryLabels,
  onPick,
}: PickerMenuProps<TCategory>) {
  return (
    <div className="space-y-3">
      {Array.from(grouped.entries()).map(([category, items]) => (
        <div key={category}>
          <h5 className="text-[10px] font-semibold uppercase text-muted-foreground tracking-wide mb-1 px-1">
            {categoryLabels[category]}
          </h5>
          <ul className="space-y-0.5">
            {items.map((item) => {
              const available = Boolean(ADJUSTMENT_SPECS[item.kind]);
              return (
                <li key={item.kind}>
                  <button
                    type="button"
                    disabled={!available}
                    onClick={() => onPick(item)}
                    className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 text-xs rounded-md min-h-[32px] text-left transition-colors ${
                      available
                        ? 'text-foreground hover:bg-accent cursor-pointer'
                        : 'text-muted-foreground/60 cursor-not-allowed'
                    }`}
                  >
                    <span>{item.title}</span>
                    {!available && (
                      <span className="text-[10px] uppercase tracking-wide opacity-70">Soon</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
